import { useState } from 'react';
import Amount, { ActionIcon, CostLabel, IconText } from './Amount';
import CollapsedCard from './CollpasedCard';
import CardBack from './CardBack';
const { Modal } = require('react-bootstrap');
const { HimejiUnits } = require('@tta/engine/Moves/Aggression');

// Playing an aggression or declaring a war: a button per card; choosing it shows every rival with the cost (twice as
// much against Gandhi), why it cannot be played against them, and warnings (the full benefit cannot be had; a war
// declared on the last turn is never resolved). For an aggression, also what the aggressor would face (strength,
// military actions, military hand by age); for a war only the strength, as weighing one up takes a look at the
// rival's board, not a pop-up.
function AggressionActions({Category, ButtonClass, GameEngine, Hand})
{
    const [choosing, setChoosing] = useState(null);
    const war = Category.ui === 'war';
    const cards = [];
    Category.options.forEach(option => {
        const entry = cards.find(c => c.id === option.choiceOf.id);
        if (entry)
        {
            entry.options.push(option);
        } else {
            cards.push({ id: option.choiceOf.id, label: option.choiceOf.label, options: [option] });
        }
    });
    return (
        <div className="tta-action-line" title={Category.title}>
            <span className="tta-action-title">{Category.label}:</span>
            {cards.map(card => {
                const available = card.options.filter(option => !option.disabled);
                return (
                    <button key={card.id} className={ButtonClass} disabled={available.length === 0}
                        title={available.length === 0 ? card.options[0].reason : undefined}
                        onClick={() => setChoosing(card)}>
                        {card.label}
                    </button>
                );
            })}
            {choosing &&
                <AggressionPicker Card={Hand.find(c => c.id === choosing.id)} Label={choosing.label}
                    Options={choosing.options} War={war}
                    Units={war ? [] : HimejiUnits(GameEngine.gameState.GetCurrentPlayerState())}
                    Strength={GameEngine.gameState.GetCurrentPlayerState().GetMilitaryStrength()}
                    Targets={war ? GameEngine.GetWarTargets(choosing.id) : GameEngine.GetAggressionTargets(choosing.id)}
                    ButtonClass={ButtonClass}
                    OnCancel={() => setChoosing(null)}
                    OnChoose={(option) => { setChoosing(null); GameEngine.submitMove(option.move); }}/>}
        </div>
    );
}

// The rival's military hand, face down: a back per card, with its age
const HandBacks = ({ages}) => ages.length === 0
    ? <span className="tta-muted">none</span>
    : <div className="tta-card-backs">{ages.map((age, index) => <CardBack key={index} age={age}/>)}</div>;

// Units: with Himeji Castle, the units it could add ({ name, strength }): one may be picked, adding its strength (and 3
// culture; sacrificed after), and each rival's option is then the one with it
export function AggressionPicker({Card, Label, Options, Targets, War, Strength, Units = [], ButtonClass, OnChoose, OnCancel})
{
    const [unit, setUnit] = useState(null);
    const added = Units.find(u => u.name === unit)?.strength ?? 0;
    const forTarget = (target) => Options.find(o => o.move.target === target.player && (o.himejiUnit ?? null) === unit);
    return (
        <Modal show onHide={OnCancel} centered dialogClassName="tta-choice-dialog">
            <Modal.Header closeButton style={{ padding: '8px 12px' }}>
                <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>{Label}: {War ? 'declare war on' : 'choose a rival'}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="tta tta-left" style={{ padding: '12px' }}>
                {/* What playing it costs beyond the actions, whoever it is played on (e.g. 8 culture during the Olympic Games) */}
                {[...new Set(Options.flatMap(option => (option.gains ?? []).filter(gain => /: -/.test(gain))))].map(gain =>
                    <div key={gain} className="tta-target-warning tta-picker-warning">
                        ⚠ You will lose <IconText text={gain.replace(/^.*: -/, '')}/> if you {War ? 'declare this war' : 'play this aggression'} ({gain.replace(/: -.*$/, '')})</div>)}
                {/* Himeji Castle: a unit added to the attack (3 culture; it is sacrificed after) */}
                {Units.length > 0 &&
                    <div className="tta-action-line tta-himeji-choice">
                        <span className="tta-action-title">Himeji Castle:</span>
                        <button className={`tta-button${unit === null ? ' dark' : ''}`} onClick={() => setUnit(null)}>No unit</button>
                        {Units.map(u =>
                            <button key={u.name} className={`tta-button${unit === u.name ? ' dark' : ''}`} onClick={() => setUnit(u.name)}
                                title="Its strength is added, you score 3 culture, and it is sacrificed after">
                                {u.name} <Amount value={u.strength} kind="strength" signed/></button>)}
                        {unit && <span className="tta-muted"><Amount value={3} kind="culture" signed/>; {unit} is sacrificed after</span>}
                    </div>}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {/* The card, and the player's own strength to set against each rival's */}
                    <div className="tta-picker-card">
                        {Card && <CollapsedCard card={Card}/>}
                        <div className="tta-picker-strength"><Amount value={Strength + added} kind="strength"/> VS</div>
                        {added > 0 && <div className="tta-muted" style={{ fontSize: '11px' }}>(+{added} {unit})</div>}
                    </div>
                    <table className="tta-target-table">
                        <thead>
                            <tr><th>Rival</th><th title="Strength"><ActionIcon kind="strength"/></th>{!War && <><th title="Military actions"><ActionIcon kind="MA"/></th><th>Military hand</th></>}<th></th></tr>
                        </thead>
                        <tbody>
                            {Targets.map(target => {
                                const option = forTarget(target);
                                const problem = target.problem ?? option?.reason;
                                return (
                                    <tr key={target.player}>
                                        <td><b>{target.name}</b></td>
                                        <td style={{ whiteSpace: 'nowrap' }}><Amount value={target.strength} kind="strength"/>
                                            {/* Strength they have only when defending (Nostradamus) */}
                                            {target.defenseExtras?.length > 0 &&
                                                <span className="tta-muted" style={{ fontSize: '11px' }}>
                                                    {' '}({target.defenseExtras.map(extra => `+${extra.value} ${extra.source}`).join(', ')})</span>}</td>
                                        {!War && <><td><Amount value={target.militaryActions} kind="MA"/></td><td><HandBacks ages={target.hand}/></td></>}
                                        <td>
                                            <button className={ButtonClass} disabled={!option || option.disabled} title={problem ?? undefined}
                                                onClick={() => OnChoose(option)}>
                                                {War ? 'Declare' : 'Play'} · <CostLabel cost={`${target.cost} MA`}/>
                                                {target.cost > target.baseCost && <span className="tta-cost-note"> (×2 Gandhi)</span>}
                                            </button>
                                            {/* (Not strong enough shows in the strengths) */}
                                            {problem && !/as strong as you/.test(problem) && <div className="tta-muted" style={{ fontSize: '12px' }}>{problem}</div>}
                                            {!problem && target.shortfall && <div className="tta-target-warning">⚠ {target.shortfall}</div>}
                                            {!problem && (target.breaks ?? []).map(text => <div key={text} className="tta-target-warning">⚠ {text}</div>)}
                                            {!problem && target.attackBonus > 0 && <div className="tta-muted" style={{ fontSize: '12px' }}>+{target.attackBonus} strength attacking them (pact)</div>}
                                            {/* (Himeji Castle: in an aggression, the most a unit adds defending; in a war, no number: a bigger unit may be built by then) */}
                                            {!problem && target.himejiDefense > 0 && <div className="tta-target-warning" title="They may add a unit's strength + 3 when defending">⚠ Himeji Castle {War ? '' : `(max +${target.himejiDefense})`}</div>}

                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {War &&
                    <div className="tta-muted" style={{ fontSize: '12px', marginTop: '8px' }}>
                        The war is resolved at the start of your next turn, by both strengths then: the stronger side wins, by the difference, and a tie does nothing.
                    </div>}
            </Modal.Body>
            <Modal.Footer className="tta" style={{ padding: '6px 12px' }}>
                <button className="tta-button" onClick={OnCancel}>Cancel</button>
            </Modal.Footer>
        </Modal>
    );
}

export default AggressionActions;
